import { ApiFilterResponse } from '@interfaces/filter.types';
import { FILTER_CONSTANTS, type FilterType } from '@constants/filterConstants';

export interface FilterConfig {
  key: string;
  title: string;
  type: FilterType;
  options?: FilterOption[];
  config?: {
    min: number;
    max: number;
    step: number;
  };
}

export interface FilterOption {
  label: string;
  value: string;
  subcategories?: FilterOption[];
}

export interface FilterSectionConfig {
  header: { title: string };
  initialOpenState: Record<string, boolean>;
  filters: Record<string, FilterConfig>;
  categories: FilterConfig[];
}

export const createUnifiedFilterConfig = (apiData: ApiFilterResponse | null): FilterSectionConfig => {
  if (!apiData) {
    return {
      header: { title: 'Filter' },
      initialOpenState: {},
      filters: {},
      categories: []
    };
  }

  const initialOpenState: Record<string, boolean> = {};
  const filters: Record<string, FilterConfig> = {};
  const categories: FilterConfig[] = [];

  const filterProcessors = {
    sort_by: () => {
      if (apiData.sort_by?.length > 0) {
        const config: FilterConfig = {
          key: 'sort_by',
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.sort_by,
          type: FILTER_CONSTANTS.FILTER_TYPES.SORT,
          options: apiData.sort_by.map(item => ({
            label: item.key || '',
            value: item.value
          }))
        };
        addFilterConfig('sort_by', config);
      }
    },

    body_type: () => {
      if (apiData.body_type?.length > 0) {
        const config: FilterConfig = {
          key: 'body_type',
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.body_type,
          type: FILTER_CONSTANTS.FILTER_TYPES.OPTIONS,
          options: apiData.body_type.map(item => ({
            label: item.key || '',
            value: item.value,
            subcategories: (item.sub_values || item.sub_segments)?.map(subItem => ({
              label: subItem.key || '',
              value: subItem.value
            })) || []
          }))
        };
        addFilterConfig('body_type', config);
      }
    },

    budget: () => {
      if (apiData.budget) {
        const config: FilterConfig = {
          key: 'budget',
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.budget,
          type: FILTER_CONSTANTS.FILTER_TYPES.RANGE,
          config: {
            min: apiData.budget.default_min_price,
            max: apiData.budget.default_max_price,
            step: FILTER_CONSTANTS.BUDGET_CONFIG.DEFAULT_STEP
          }
        };
        addFilterConfig('budget', config);
      }
    },

    brand_model: () => {
      if (apiData.brand_model?.length > 0) {
        const config: FilterConfig = {
          key: 'brand_model',
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.brand_model,
          type: FILTER_CONSTANTS.FILTER_TYPES.OPTIONS,
          options: apiData.brand_model.map(brand => ({
            label: brand.brand_name || '',
            value: brand.brand_name,
            subcategories: brand.models?.map(model => ({
              label: model.model_name || '',
              value: model.model_name
            })) || []
          }))
        };
        addFilterConfig('brand_model', config);
      }
    },

    fuel_type: () => {
      if (apiData.fuel_type?.length > 0) {
        const config: FilterConfig = {
          key: 'fuel_type',
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.fuel_type,
          type: FILTER_CONSTANTS.FILTER_TYPES.OPTIONS,
          options: apiData.fuel_type.map(item => ({
            label: item.key || '',
            value: item.value
          }))
        };
        addFilterConfig('fuel_type', config);
      }
    },

    transmission: () => {
      if (apiData.transmission?.length > 0) {
        const config: FilterConfig = {
          key: 'transmission',
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.transmission,
          type: FILTER_CONSTANTS.FILTER_TYPES.OPTIONS,
          options: apiData.transmission.map(item => ({
            label: item.key || '',
            value: item.value
          }))
        };
        addFilterConfig('transmission', config);
      }
    },

    seat_capacity: () => {
      if (apiData.seat_capacity && Array.isArray(apiData.seat_capacity) && apiData.seat_capacity.length > 0) {
        const config: FilterConfig = {
          key: 'seat_capacity',
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.seat_capacity,
          type: FILTER_CONSTANTS.FILTER_TYPES.OPTIONS,
          options: apiData.seat_capacity.map((item: any) => ({
            label: item.key,
            value: item.value
          }))
        };
        addFilterConfig('seat_capacity', config);
      }
    },

    feature: () => {
      if (apiData.feature && Array.isArray(apiData.feature) && apiData.feature.length > 0) {
        const config: FilterConfig = {
          key: 'feature',
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.feature,
          type: FILTER_CONSTANTS.FILTER_TYPES.OPTIONS,
          options: apiData.feature.map((item: any) => ({
            label: item.key,
            value: item.value
          }))
        };
        addFilterConfig('feature', config);
      }
    }
  };

  function addFilterConfig(key: string, config: FilterConfig) {
    initialOpenState[key] = false;
    filters[key] = config;
    categories.push(config);
  }

  Object.keys(filterProcessors).forEach(key => {
    (filterProcessors as any)[key]();
  });

  Object.keys(apiData).forEach(key => {
    if (FILTER_CONSTANTS.EXCLUDED_FILTER_KEYS.includes(key as any) || 
        FILTER_CONSTANTS.STATIC_FILTER_KEYS.includes(key as any)) {
      return;
    }

    const value = (apiData as any)[key];
    
    if (Array.isArray(value) && value.length > 0) {
      const hasKeyValue = value.every(item => item && typeof item === 'object' && 'key' in item && 'value' in item);
      
      if (hasKeyValue) {
        const config: FilterConfig = {
          key,
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS[key as keyof typeof FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS] || 
                 key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          type: FILTER_CONSTANTS.FILTER_TYPES.OPTIONS,
          options: value.map((item: any) => ({
            label: item.key,
            value: item.value,
            subcategories: (item.sub_values || item.sub_segments)?.map((subItem: any) => ({
              label: subItem.key,
              value: subItem.value
            })) || []
          }))
        };
        addFilterConfig(key, config);
      }
    }
  });

  return {
    header: { title: 'Filter' },
    initialOpenState,
    filters,
    categories
  };
};

export const createMobileFilterCategories = (
  apiData: ApiFilterResponse | null,
  includeExpressDelivery: boolean = true
): Array<[string, { title: string; type: string }]> => {
  if (!apiData) return [];

  const categories: Array<[string, { title: string; type: string }]> = [];

  if (includeExpressDelivery) {
    categories.push(['is_express_delivery', { 
      title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.is_express_delivery, 
      type: FILTER_CONSTANTS.FILTER_TYPES.TOGGLE 
    }]);
  }

  Object.keys(apiData).forEach(key => {
    if (key === 'budget') {
      const value = (apiData as any)[key];
      if (value && typeof value === 'object') {
        categories.push([key, { 
          title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.budget, 
          type: FILTER_CONSTANTS.FILTER_TYPES.RANGE 
        }]);
      }
      return;
    }

    if (FILTER_CONSTANTS.EXCLUDED_FILTER_KEYS.includes(key as any)) {
      return;
    }

    const value = (apiData as any)[key];

    if (Array.isArray(value) && value.length > 0) {
      const title = FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS[key as keyof typeof FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS] || 
                   key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      
      categories.push([key, { 
        title, 
        type: FILTER_CONSTANTS.FILTER_TYPES.OPTIONS 
      }]);
    }
  });

  return categories;
};

export const createFilterChipsConfig = (apiData: ApiFilterResponse | null) => {
  if (!apiData) return [];

  const chips: Array<{ key: string; title: string }> = [];

  Object.keys(apiData).forEach(key => {
    if (FILTER_CONSTANTS.EXCLUDED_FILTER_KEYS.includes(key as any)) {
      return;
    }

    const value = (apiData as any)[key];

    if (Array.isArray(value) && value.length > 0) {
      chips.push({
        key,
        title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS[key as keyof typeof FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS] || 
               key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      });
    }

    if (key === 'budget' && value && typeof value === 'object') {
      chips.push({
        key,
        title: FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS.budget
      });
    }
  });

  return chips;
}; 